import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InvoiceColumns from 'src/assets/static/receipt_details/InvoiceColumns';
import GridWrapper from 'src/components/GridWrapper';
import useCompany from 'src/hooks/useCompany';
import {
  useCheckLinkedReceiptQuery,
  useGetInvoicesQuery,
  useLinkInvoiceToReceiptMutation,
} from 'src/services/invoices';
import { InvoiceItems } from 'src/types/Invoices';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { transformResponse } from 'src/utils/transformation';

import { SortDescriptor, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import LoadingPanel from 'src/components/LoadingPanel';
import Button from 'src/components/design-system/Button';
import ConfirmLinkModal from './ConfirmLinkModal';

interface ManageInvoiceLinkProps {
  showModal: (modalName: string) => void;
}

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const ManageInvoiceLinks: React.FC<ManageInvoiceLinkProps> = ({ showModal }) => {
  const INVOICE_DATA_ITEM: string = 'receiptId';
  const [searchParams] = useSearchParams();
  const initialInvoiceDataState = {
    skip: 0,
    take: 20,
    skipCalculatingTotalRecordsCount: false,
    sort: [
      {
        field: 'receiptId',
        dir: 'asc',
      },
    ],
    filters: [{ field: 'poId', value: searchParams.get('poid'), operator: 'eq' }],
  };
  const initialSort: Array<SortDescriptor> = [{ field: 'invoiceNumber', dir: 'asc' }];
  const { company: selectedCompany } = useCompany();
  const [invoiceDataState, setInvoiceDataState] = useState<any>(initialInvoiceDataState);
  const [invoiceProcessData, setInvoiceProcessData] = useState<InvoiceItems[]>([]);
  const [openConfirmLinkModal, setOpenConfirmLinkModal] = useState(false);
  const [, setSort] = useState(initialSort);
  const [prevReceiptId, setPrevReceiptId] = useState<string | undefined>();
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState<InvoiceItems>({} as InvoiceItems);
  const [checkLinkedEnabled, setCheckLinkedEnabled] = useState(false);
  const [count, setCount] = useState(0);

  // Fetch invoice data using TanStack Query
  const { data: invoiceData, isLoading: invoiceLoading } = useGetInvoicesQuery({
    companyId: selectedCompany.companyId,
    body: modifyRequest(invoiceDataState),
  });

  const body: State = useMemo(
    () => ({
      skip: 0,
      take: 1,
      sort: [],
      filters: [
        {
          field: 'recvReceiptReceiptId',
          value: selectedInvoiceRow?.receiptId?.toString() || '',
          operator: 'eq',
        },
      ],
    }),
    [selectedInvoiceRow?.receiptId]
  );

  // Check if invoice is linked to another receipt
  const { data: linkedReceiptData } = useCheckLinkedReceiptQuery({
    companyId: selectedCompany.companyId,
    body,
    enabled: checkLinkedEnabled,
  });

  // Link invoice to receipt mutation
  const { mutate: linkInvoice } = useLinkInvoiceToReceiptMutation({
    onSuccess: () => {
      showModal(`Link a document to Receipt ${searchParams.get('recid')}`);
    },
  });

  // Update invoice process data when invoice data changes
  useEffect(() => {
    if (invoiceData) {
      const transformedData = transformResponse(invoiceData.items, 'LINK_INVOICES');
      setInvoiceProcessData(transformedData);
      setCount(invoiceData.totalRecords);
    }
  }, [invoiceData]);

  // Handle linked receipt check result
  useEffect(() => {
    if (linkedReceiptData && checkLinkedEnabled) {
      setCheckLinkedEnabled(false);
      if (linkedReceiptData.items.length === 0) {
        linkInvoice({
          receiptId: searchParams.get('recid') || '',
          invoiceId: selectedInvoiceRow?.receiptId,
        });
      } else {
        setPrevReceiptId(linkedReceiptData.items[0]?.receivingReceiptId);
        setOpenConfirmLinkModal(true);
      }
    }
  }, [linkedReceiptData, checkLinkedEnabled, linkInvoice, searchParams, selectedInvoiceRow]);

  const onInvoiceDataStateChange = (e: GridDataStateChangeEvent) => {
    setInvoiceDataState(modifyRequest(e.dataState));
  };
  //================================================Selection======================================
  const onSelectionInvoiceChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setInvoiceProcessData((prev) => {
        const previousItems = [...prev];
        const index = event.startRowIndex;
        previousItems[index].selected = !previousItems[index].selected;
        return previousItems;
      });
      setSelectedInvoiceRow(
        invoiceProcessData?.filter((invoice: InvoiceItems) => invoice.selected === true)[0]
      );
    },
    [invoiceProcessData, setSelectedInvoiceRow]
  );

  // selection row logic

  //=====================================================Selection=====================================

  const onLinkInvoiceClick = () => {
    if (selectedInvoiceRow?.receiptId) {
      setCheckLinkedEnabled(true);
    }
  };
  return (
    <>
      <div>
        <div style={{ marginBottom: '50px' }}>
          <div style={{ lineHeight: '1.5', marginBottom: '20px' }}>
            <b>Choose a document to link to this Receipt.</b>
          </div>
          <div>
            {invoiceLoading ? <LoadingPanel /> : ''}
            <ConfirmLinkModal
              id="completeModal"
              visible={openConfirmLinkModal}
              width="400px"
              closeModal={() => setOpenConfirmLinkModal(false)}
              closeParentModal={showModal}
              receiptId={searchParams.get('recid') || ''}
              title="Link Selected Invoice"
              height="300px"
              invoiceId={selectedInvoiceRow?.receiptId || ''}
              invoiceNumber={selectedInvoiceRow?.invoiceNumber}
              previouslyLinkedReceiptId={prevReceiptId}
            />
            <GridWrapper
              columns={InvoiceColumns}
              dataSource={invoiceProcessData}
              dataState={invoiceDataState}
              sortable
              resizable
              height="50vh"
              total={count}
              showColumnMenu
              DataItemKey={INVOICE_DATA_ITEM}
              selectable={selectable}
              onDataStateChange={onInvoiceDataStateChange}
              pageable={gridPagerSettings}
              onSelectionChange={onSelectionInvoiceChange}
              onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
              tableTextId="invoices"
            />
          </div>
        </div>
        <DialogActionsBar>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              color="info"
              disabled={
                invoiceProcessData?.filter((invoice: InvoiceItems) => invoice.selected === true)
                  .length !== 1
              }
              onClick={onLinkInvoiceClick}
            >
              Link selected Document
            </Button>
            <Button color="info" onClick={() => showModal('Receipt History')}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </div>
    </>
  );
};

export default ManageInvoiceLinks;
